// @djsem/render-state — Release Studio Starter (Prompt 22).
//
// Boundary-first export layer: models export JOBS and starter export MANIFESTS for social
// teasers, track visual previews, show-pack previews, and rehearsal capture references.
// NO GPU rendering, NO paid render credits, NO Stripe, NO marketplace (rule 10 + rules 12).
// `estimatedCredits` exists as an advisory placeholder; `billed` is a hard `false` literal
// on the type so no code path can ever claim payment succeeded. Renderers consume meaning;
// this module never invents meaning or claims rights clearance (rules 7, 11–12).
//
// Naming note: there is an older, incompatible `DJSEMReleaseExportManifest` shape in
// @djsem/shared/worldPacks.ts (asset-bundle style). To honor rule 2 (no duplicate
// incompatible schemas), the new Release Studio manifest is exported as
// `DJSEMReleaseStudioExportManifest`. The old shape remains untouched.

import { type ValidationResult, type DJSEMValidationResult, toDJSEMValidationResult, stableStringify } from "@djsem/shared";
import { SeqCounter } from "@djsem/shared";

// Local deterministic hash (same FNV-1a formulation used elsewhere in the codebase).
// Kept local to preserve the acyclic package graph — render-state does not depend on analysis-contract.
function deterministicHash(value: unknown): string {
  const s = stableStringify(value);
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
  return h.toString(16).padStart(8, "0");
}

// ---- contracts --------------------------------------------------------------

/** The kind of release export a {@link DJSEMReleaseExportJob} produces. */
export type DJSEMReleaseExportProfile =
  | "social_teaser" | "track_visual_preview" | "show_pack_preview" | "rehearsal_capture_reference";

/** Lifecycle status of a release export job. */
export type DJSEMExportJobStatus = "draft" | "queued" | "running" | "completed" | "failed" | "cancelled";

/** One output asset produced (or pending) for a release export job. */
export interface DJSEMReleaseOutputAsset {
  id: string;
  assetType: "video" | "image" | "json_manifest" | "thumbnail" | "reference_capture";
  path?: string;
  r2Key?: string;
  hash?: string;
  status: "pending" | "available" | "failed";
  warnings: string[];
}

/**
 * An export job modeling a release-asset request (social teaser, track visual preview,
 * show-pack preview, or rehearsal capture reference). No GPU rendering happens here;
 * `renderCreditEstimate.billed` is a hard `false` literal — no code path ever bills.
 */
export interface DJSEMReleaseExportJob {
  id: string;
  profile: DJSEMReleaseExportProfile;
  status: DJSEMExportJobStatus;
  sourceSnapshotId?: string;
  sourceShowPackId?: string;
  brandWorldId?: string;
  visualSceneIds: string[];
  lightingSceneIds: string[];
  outputManifestR2Key?: string;
  outputAssetRefs: DJSEMReleaseOutputAsset[];
  renderCreditEstimate?: {
    estimatedCredits: number;
    billed: false; // literal: no code path ever bills. rule 12.
    notes: string[];
  };
  rightsWarnings: string[];
  createdAt: string;
  updatedAt: string;
}

/**
 * Export manifest for a {@link DJSEMReleaseExportJob}. Named `DJSEMReleaseStudioExportManifest`
 * (not `DJSEMReleaseExportManifest`) to avoid clashing with the older, incompatible
 * asset-bundle manifest shape in `@djsem/shared`.
 */
export interface DJSEMReleaseStudioExportManifest {
  id: string;
  jobId: string;
  profile: DJSEMReleaseExportProfile;
  createdAt: string;
  sourceRefs: Record<string, string>;
  outputAssets: DJSEMReleaseOutputAsset[];
  rightsWarnings: string[];
  validation: DJSEMValidationResult;
}

/**
 * Rights snapshot of the sources a job pulls from. Passed in by the caller — this module
 * never decides rights, only aggregates what it's told.
 */
export interface DJSEMReleaseSourceRightsSnapshot {
  audioRights?: "unknown" | "owned" | "licensed" | "restricted";
  lyricsRights?: "unknown" | "owned" | "licensed" | "restricted";
  stemRights?: "unknown" | "owned" | "licensed" | "restricted";
  brandRights?: "unknown" | "owned" | "licensed" | "restricted";
  showPackValidation?: DJSEMValidationResult;
}

// ---- inputs -----------------------------------------------------------------

/** Input for {@link ReleaseStudioService.createExportJob}. */
export interface CreateExportJobInput {
  id?: string;
  profile: DJSEMReleaseExportProfile;
  sourceSnapshotId?: string;
  sourceShowPackId?: string;
  brandWorldId?: string;
  visualSceneIds?: string[];
  lightingSceneIds?: string[];
  rights?: DJSEMReleaseSourceRightsSnapshot;
  createdAt?: string;
}
/** Input for {@link ReleaseStudioService.createExportManifest}. */
export interface CreateExportManifestInput { job: DJSEMReleaseExportJob; createdAt?: string; }
/** Input for {@link ReleaseStudioService.estimateRenderCredits}. */
export interface EstimateRenderCreditsInput { job: DJSEMReleaseExportJob; estimatedCredits: number; notes?: string[]; }
/** Input for {@link ReleaseStudioService.completeMockExport}. */
export interface CompleteMockExportInput { job: DJSEMReleaseExportJob; outputs: DJSEMReleaseOutputAsset[]; completedAt?: string; }
/** Input for {@link ReleaseStudioService.failExportJob}. */
export interface FailExportJobInput { job: DJSEMReleaseExportJob; error: string; failedAt?: string; }

const AT = "1970-01-01T00:00:00.000Z";
const PUBLIC_READY: DJSEMReleaseExportProfile[] = ["social_teaser", "show_pack_preview"];

// ---- service (class-based; hard rule 12) ------------------------------------

const _seq = new SeqCounter();
/**
 * Boundary-first export layer: models export jobs and starter export manifests for social
 * teasers, track visual previews, show-pack previews, and rehearsal capture references. No
 * GPU rendering, no paid render credits, no Stripe, no marketplace.
 */
export class ReleaseStudioService {
  /**
   * Creates a new draft export job, aggregating any rights warnings from the supplied
   * rights snapshot. Every job carries an honest no-billing estimate from birth.
   * @param input - the export profile, sources, brand world, scene ids, and optional rights.
   * @returns the new draft export job.
   */
  createExportJob(input: CreateExportJobInput): DJSEMReleaseExportJob {
    const rightsWarnings = this.aggregateRights(input.profile, input.rights);
    const at = input.createdAt ?? AT;
    return {
      id: input.id ?? `rls_${_seq.next()}`,
      profile: input.profile, status: "draft",
      sourceSnapshotId: input.sourceSnapshotId, sourceShowPackId: input.sourceShowPackId,
      brandWorldId: input.brandWorldId,
      visualSceneIds: input.visualSceneIds ?? [], lightingSceneIds: input.lightingSceneIds ?? [],
      outputAssetRefs: [], rightsWarnings, createdAt: at, updatedAt: at,
      // QA fix: every job carries the honest no-billing marker from birth, not only after
      // estimateCredits runs. billed stays a hard `false` literal (rule 12).
      renderCreditEstimate: { estimatedCredits: 0, billed: false, notes: ["Estimate pending; no credits billed. No payment integration."] },
    };
  }

  /**
   * Validates a job: id present, at least one source, and no `BLOCK:`-prefixed rights warning.
   * @param job - the export job to validate.
   * @returns a validation result listing any issues.
   */
  validateExportJob(job: DJSEMReleaseExportJob): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (!job?.id) issues.push({ path: "$.id", message: "id required" });
    // require at least one source
    if (!job?.sourceSnapshotId && !job?.sourceShowPackId) issues.push({ path: "$.source", message: "either sourceSnapshotId or sourceShowPackId is required" });
    // any rights warning that starts with "BLOCK:" is a hard block
    for (const w of job?.rightsWarnings ?? []) if (w.startsWith("BLOCK:")) issues.push({ path: "$.rightsWarnings", message: w });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "release_job");
  }

  /**
   * Builds a deterministic export manifest for a job: same inputs always yield the same
   * manifest id (content-hash derived).
   * @param input - the job to build a manifest for and an optional creation timestamp.
   * @returns the assembled export manifest, including the job's validation result.
   */
  createExportManifest(input: CreateExportManifestInput): DJSEMReleaseStudioExportManifest {
    const validation = this.validateExportJob(input.job);
    const sourceRefs: Record<string, string> = {};
    if (input.job.sourceSnapshotId) sourceRefs.snapshotId = input.job.sourceSnapshotId;
    if (input.job.sourceShowPackId) sourceRefs.showPackId = input.job.sourceShowPackId;
    if (input.job.brandWorldId) sourceRefs.brandWorldId = input.job.brandWorldId;
    // deterministic manifest id from content hash — same inputs always yield same id
    const bodyHash = deterministicHash({
      jobId: input.job.id, profile: input.job.profile, sourceRefs,
      visualSceneIds: [...input.job.visualSceneIds].sort(),
      lightingSceneIds: [...input.job.lightingSceneIds].sort(),
      outputAssets: [...input.job.outputAssetRefs].sort((a, b) => a.id.localeCompare(b.id)),
      rightsWarnings: input.job.rightsWarnings,
    });
    return {
      id: `rlm_${input.job.id}_${bodyHash}`,
      jobId: input.job.id,
      profile: input.job.profile,
      createdAt: input.createdAt ?? input.job.updatedAt,
      sourceRefs,
      outputAssets: [...input.job.outputAssetRefs].sort((a, b) => a.id.localeCompare(b.id)),
      rightsWarnings: [...input.job.rightsWarnings],
      validation,
    };
  }

  /**
   * Sets an advisory render-credit estimate on a job. `billed` remains a hard `false`
   * literal — no payment integration exists.
   * @param input - the job, the estimated credit count, and optional notes.
   * @returns the updated job with its estimate set.
   */
  estimateRenderCredits(input: EstimateRenderCreditsInput): DJSEMReleaseExportJob {
    return {
      ...input.job,
      renderCreditEstimate: {
        estimatedCredits: Math.max(0, input.estimatedCredits),
        billed: false, // hard literal
        notes: [...(input.notes ?? []), "Estimate only; no credits billed. No payment integration."],
      },
      updatedAt: input.job.updatedAt,
    };
  }

  /**
   * Marks a job completed with the given (mock) output assets.
   * @param input - the job, its output assets, and an optional completion timestamp.
   * @returns the updated, completed job.
   */
  completeMockExport(input: CompleteMockExportInput): DJSEMReleaseExportJob {
    return { ...input.job, status: "completed", outputAssetRefs: input.outputs, updatedAt: input.completedAt ?? AT };
  }

  /**
   * Marks a job failed and appends the error to its rights/warning trail.
   * @param input - the job, the error message, and an optional failure timestamp.
   * @returns the updated, failed job.
   */
  failExportJob(input: FailExportJobInput): DJSEMReleaseExportJob {
    return {
      ...input.job, status: "failed",
      rightsWarnings: [...input.job.rightsWarnings, `error: ${input.error}`],
      updatedAt: input.failedAt ?? AT,
    };
  }

  // ---- private --------------------------------------------------------------
  // Rights aggregator. BLOCK: prefix = hard block for public-ready profiles; plain text = warn.
  // Never claims clearance. Never invents rights.
  private aggregateRights(profile: DJSEMReleaseExportProfile, rights?: DJSEMReleaseSourceRightsSnapshot): string[] {
    const out: string[] = [];
    if (!rights) return out;
    const isPublic = PUBLIC_READY.includes(profile);
    const check = (kind: string, s?: "unknown" | "owned" | "licensed" | "restricted") => {
      if (!s) return;
      if (s === "restricted") out.push(isPublic ? `BLOCK: ${kind} rights restricted; blocked for public-ready export.` : `${kind} rights restricted; local/preview only.`);
      else if (s === "unknown") out.push(`${kind} rights unknown; review before public export.`);
    };
    check("audio", rights.audioRights);
    check("lyrics", rights.lyricsRights);
    check("stem", rights.stemRights);
    check("brand", rights.brandRights);
    if (rights.showPackValidation && !rights.showPackValidation.ok) {
      const msg = "show-pack validation failed; " + rights.showPackValidation.issues.map((i) => i.message).join("; ");
      out.push(isPublic ? `BLOCK: ${msg}` : msg);
    }
    return out;
  }
}
